import { match } from 'ts-pattern'

import {
  ConditionalFunctionString,
  PERCENTAGE_UNIT
} from '../../conditions.types'
import type { SemanticType } from '../../../semanticTypes'
import {
  allDiscountsEqual,
  findProductBillingFrequencies,
  findProductBillingTypes,
  findProductDiscounts,
  findProductPricingModels,
  parseAmount
} from '../runtimeHelpers'
import { semanticTypeRuntimePath } from './utils'
// The user-facing pricing-model union (with SEAT_BASED_LINEAR /
// SEAT_BASED_GRADUATED separated) lives in the pricing editor's domain
// types — `@sequencehq/api`'s `PricingType` is the backend-collapsed
// version that doesn't separate the seat-based variants, so it isn't a
// substitute. Re-using the editor's `PricingModel` keeps the two
// surfaces in lockstep: any new model added there flows here for free, and we
// retain the consistent user experience.
//
// On a side note - whenever we do a new API version, reivising pricing models
// to be clearer and more aligned with the UX would be evry helpful.
import type { PricingModel } from 'common/drawers/PricingEditor'
import type { BillingType, SelectableBillingFrequency } from '@sequencehq/utils'

/**
 * Configuration for the product-discount comparison factory. The
 * `currencySemanticType` is the upstream value holding the quote's
 * currency code; when set, the runtime expression for a currency unit
 * also asserts `$["…currency"] === <chosenUnit>` so a NOMINAL discount
 * in EUR doesn't incorrectly satisfy a "less than $100" check. The
 * discounts map itself is read from the operator's effective
 * `valueSemanticType` via the `property` runtime path.
 */
export type ProductDiscountFunctionStringConfig = {
  currencySemanticType?: SemanticType
}

/**
 * Shape stored on the condition's `value` for the product-discount operator.
 * `productId` picks which product's discounts to inspect; `operator`
 * selects the sub-comparison (less than / more than / equals); `amount` and
 * `unit` mirror the amount-with-unit shape used by other amount operators.
 */
const PRODUCT_DISCOUNT_OPERATORS = ['LESS_THAN', 'MORE_THAN', 'EQUALS'] as const

type ProductDiscountOperator = (typeof PRODUCT_DISCOUNT_OPERATORS)[number]

type ProductDiscountValue = {
  productId?: string
  operator?: ProductDiscountOperator
  amount?: string | number
  unit?: string
  /** PRODUCT.HAS_DISCOUNT only: whether the user expects a discount. */
  hasDiscount?: boolean
}

const OPERATOR_SYMBOLS: Record<
  Exclude<ProductDiscountOperator, 'EQUALS'>,
  '<' | '>'
> = {
  LESS_THAN: '<',
  MORE_THAN: '>'
}

const isProductDiscountOperator = (
  operator: unknown
): operator is ProductDiscountOperator =>
  PRODUCT_DISCOUNT_OPERATORS.includes(operator as ProductDiscountOperator)

const readProductDiscountValue = (value: unknown): ProductDiscountValue => {
  if (typeof value === 'object' && value !== null) {
    const candidate = value as ProductDiscountValue
    return {
      ...candidate,
      operator: isProductDiscountOperator(candidate.operator)
        ? candidate.operator
        : undefined
    }
  }
  return {}
}

/**
 * Factory for the single PRODUCT.DISCOUNT operator. Dispatches on the
 * `operator` field of the stored value: PERCENTAGE units match
 * PERCENTAGE-type discounts; currency units match NOMINAL-type discounts
 * and optionally assert the quote currency equals the chosen unit. Returns
 * `false` when the operator is missing or unrecognised — validation
 * surfaces the missing field in the editor.
 *
 * EQUALS describes the product as a whole, so every matching discount must
 * equal the value. LESS_THAN and MORE_THAN are risk checks, so any matching
 * discount may trigger them. All three fail when no applicable discount exists.
 */
export const productDiscount =
  (
    config: ProductDiscountFunctionStringConfig = {}
  ): ConditionalFunctionString =>
  ({ property, value }) => {
    const { productId, amount, unit, operator } =
      readProductDiscountValue(value)
    if (!operator) return 'false'

    const discounts = findProductDiscounts(property, JSON.stringify(productId))
    const discountType = unit === PERCENTAGE_UNIT ? 'PERCENTAGE' : 'NOMINAL'
    const currencyAssertion =
      discountType === 'NOMINAL' && unit && config.currencySemanticType
        ? `${semanticTypeRuntimePath(config.currencySemanticType)} === ${JSON.stringify(unit)}`
        : undefined

    return match(operator)
      .with('EQUALS', () =>
        allDiscountsEqual(
          discounts,
          JSON.stringify({ type: discountType, amount }),
          currencyAssertion ?? 'true'
        )
      )
      .with('LESS_THAN', 'MORE_THAN', comparisonOperator => {
        const symbol = OPERATOR_SYMBOLS[comparisonOperator]
        const amountCheck = `${parseAmount('d.discountValue')} ${symbol} ${amount}`
        const currencyCheck = currencyAssertion
          ? ` && ${currencyAssertion}`
          : ''
        return `${discounts}.some(function(d) { return d.type === ${JSON.stringify(discountType)}${currencyCheck} && ${amountCheck}; })`
      })
      .exhaustive()
  }

/**
 * Function string for PRODUCT.HAS_DISCOUNT. The stored `hasDiscount`
 * boolean inverts the check: `true` matches when the product has any
 * applicable discount; `false` matches when it has none (including
 * products missing from `productDiscounts` because they aren't on the
 * quote). Reads the discounts map from the operator's effective
 * `valueSemanticType` via the `property` runtime path.
 */
export const productHasDiscount: ConditionalFunctionString = ({
  property,
  value
}) => {
  const { productId, hasDiscount } = readProductDiscountValue(value)
  const discounts = findProductDiscounts(property, JSON.stringify(productId))
  // `hasDiscount` is a tri-state: `true` (asserts presence), `false`
  // (asserts absence), or `undefined` (form just opened, defaults to
  // presence). Treating "anything but explicit false" as "wants a
  // discount" preserves that default — naming it makes the intent
  // clearer than `=== false` at the comparison site.
  const wantsDiscount = hasDiscount !== false
  const operator = wantsDiscount ? '>' : '==='
  return `${discounts}.length ${operator} 0`
}

/**
 * Re-export of the pricing editor's `PricingModel` union, scoped to the
 * automations module so call sites don't reach across modules for the
 * type. SEAT_BASED_LINEAR / SEAT_BASED_GRADUATED are collapsed in the
 * upstream data into a single `pricingType: 'SEAT_BASED'` entry; the
 * runtime expression below disambiguates them via the `hasTiers` field,
 * matching the same rule the pricing editor uses (presence of
 * tiers ⇒ graduated).
 */
export type ProductPricingModel = PricingModel

type PricingModelSubOperator = 'IS' | 'IS_NOT'

type ProductPricingModelValue = {
  productId?: string
  pricingModel?: ProductPricingModel
  /** Sub-operator: IS matches when every price uses the model; IS_NOT
   *  matches when at least one price uses a different model. */
  operator?: PricingModelSubOperator
}

const readProductPricingModelValue = (
  value: unknown
): ProductPricingModelValue => {
  if (typeof value === 'object' && value !== null) {
    return value as ProductPricingModelValue
  }
  return {}
}

// Maps a user-facing pricing model to the runtime predicate string the
// expression evaluates against each `{ pricingType, hasTiers }` entry.
// Conventions worth calling out:
// - STANDARD covers both `FIXED` and `ONE_TIME` backend pricing types
//   because `getPricingModelLabel` renders both as "Fixed" in the
//   pricing editor — the condition must agree.
// - SEAT_BASED splits on the tiers flag, matching the same rule
//   `getPricingModelLabel` uses to decide LINEAR vs GRADUATED.
const PRICING_MODEL_PREDICATES: Record<ProductPricingModel, string> = {
  STANDARD: '(e.pricingType === "FIXED" || e.pricingType === "ONE_TIME")',
  LINEAR: 'e.pricingType === "LINEAR"',
  VOLUME: 'e.pricingType === "VOLUME"',
  GRADUATED: 'e.pricingType === "GRADUATED"',
  PACKAGE: 'e.pricingType === "PACKAGE"',
  SEAT_BASED_LINEAR: 'e.pricingType === "SEAT_BASED" && !e.hasTiers',
  SEAT_BASED_GRADUATED: 'e.pricingType === "SEAT_BASED" && !!e.hasTiers'
}

/**
 * Function string for PRODUCT.PRICING_MODEL.
 *
 * - IS: passes when every price for the chosen product uses the selected
 *   pricing model.
 * - IS_NOT: passes when at least one price for the chosen product uses a
 *   different pricing model. (A quote where one phase deviates from the
 *   selected model is still a quote where "the pricing model is not X"
 *   for that product.)
 *
 * A product with no prices in the quote (including a product missing
 * from the upstream phases entirely) fails the check under both
 * operators — there's nothing to assert against. Returns `false` when
 * the pricing model or sub-operator is missing or unrecognised so
 * validation surfaces the gap in the editor. Reads the phases array
 * from the operator's effective `valueSemanticType` via the `property`
 * runtime path.
 */
export const productPricingModel: ConditionalFunctionString = ({
  property,
  value
}) => {
  const { productId, pricingModel, operator } =
    readProductPricingModelValue(value)
  if (!pricingModel) return 'false'
  const predicate = PRICING_MODEL_PREDICATES[pricingModel]
  if (!predicate) return 'false'
  if (operator !== 'IS' && operator !== 'IS_NOT') return 'false'

  const entries = findProductPricingModels(property, JSON.stringify(productId))
  // IS  → every price uses the selected model.
  // IS_NOT → at least one price does NOT use the selected model. Using
  //          `some(!predicate)` rather than `every(!predicate)` so a
  //          quote with one deviating phase still satisfies "is not X".
  const iteration = operator === 'IS' ? 'every' : 'some'
  const elementPredicate = operator === 'IS' ? predicate : `!(${predicate})`
  return `(${entries}.length > 0 && ${entries}.${iteration}(function(e) { return ${elementPredicate}; }))`
}

type BillingFrequencySubOperator = 'IS' | 'IS_NOT'

type ProductBillingFrequencyValue = {
  productId?: string
  billingFrequency?: SelectableBillingFrequency
  /** Sub-operator: IS matches when every price uses the frequency; IS_NOT
   *  matches when at least one price uses a different one. */
  operator?: BillingFrequencySubOperator
}

const readProductBillingFrequencyValue = (
  value: unknown
): ProductBillingFrequencyValue => {
  if (typeof value === 'object' && value !== null) {
    return value as ProductBillingFrequencyValue
  }
  return {}
}

/**
 * Function string for PRODUCT.BILLING_FREQUENCY. IS passes when every price
 * for the chosen product bills on the selected frequency; IS_NOT passes when
 * at least one bills on a different one, so a single deviating phase is
 * enough. Returns `false` while the frequency or sub-operator is still blank,
 * and the editor's validation asks for them.
 *
 * A product with no prices on the quote passes either way. The condition has
 * nothing to say about a product that isn't being sold, so it steps aside
 * rather than blocking the quote — pair it with a `Products includes` row when
 * the approval should only apply to quotes carrying the product. Note this
 * makes "is" and "is not" both true for an absent product, so they stop being
 * exact opposites.
 */
export const productBillingFrequency: ConditionalFunctionString = ({
  property,
  value
}) => {
  const { productId, billingFrequency, operator } =
    readProductBillingFrequencyValue(value)
  if (!billingFrequency) return 'false'
  if (operator !== 'IS' && operator !== 'IS_NOT') return 'false'

  const frequencies = findProductBillingFrequencies(
    property,
    JSON.stringify(productId)
  )
  const { iteration, comparison } = match(operator)
    .with('IS', () => ({ iteration: 'every', comparison: '===' }))
    .with('IS_NOT', () => ({ iteration: 'some', comparison: '!==' }))
    .exhaustive()
  return `(${frequencies}.length === 0 || ${frequencies}.${iteration}(function(f) { return f ${comparison} ${JSON.stringify(billingFrequency)}; }))`
}

type ProductBillingTypeValue = {
  productId?: string
  billingType?: BillingType
}

const readProductBillingTypeValue = (
  value: unknown
): ProductBillingTypeValue => {
  if (typeof value === 'object' && value !== null) {
    return value as ProductBillingTypeValue
  }
  return {}
}

/**
 * Function string for PRODUCT.BILLING_TYPE. Passes when every price for the
 * chosen product bills on the selected type.
 *
 * A product with no prices on the quote passes, the same way billing frequency
 * does: the condition has nothing to say about a product that isn't being
 * included, so it steps aside rather than blocking the quote. Pair it with a
 * `Products includes` row when the approval should only apply to quotes
 * carrying the product. The empty check is spelled out rather than left to
 * `every` being vacuously true, so it reads like its neighbour.
 *
 * "Is" is the only comparison, so the stored sub-operator is ignored rather
 * than checked. Reading it was worse than useless: a row that somehow held
 * "is not" returned a flat `false`, which took an absent product down with it.
 *
 * Pricing model still fails closed for an absent product — the ADR gave the
 * special case to billing frequency and billing type only.
 * ADR: https://app.notion.com/p/sequencehq/Incident-io-further-approval-conditions-3e29b9b6e5c680a78cd5f769dc0a064d
 */
export const productBillingType: ConditionalFunctionString = ({
  property,
  value
}) => {
  const { productId, billingType } = readProductBillingTypeValue(value)
  if (!billingType) return 'false'

  const types = findProductBillingTypes(property, JSON.stringify(productId))
  return `(${types}.length === 0 || ${types}.every(function(t) { return t === ${JSON.stringify(billingType)}; }))`
}
